import React, { useState, useEffect } from "react";
import userServices from "../../components/Services/UserService";
import { showErrorToast, showSuccessToast } from "../../components/Constant/Notification";
import { validateInput } from "../../utils/regexValidators";
import { useNavigate } from "react-router-dom";
import teamService from "../../components/Services/TeamService";
import { getBasePathByUserRole } from "../../utils/roleRoutes";
import UserService from "../../GlobalSetupComponents/Services/UserService";
import { managementDepartmentLabel, salesDepartmentLabel } from "../../GlobalSetupComponents/Enum/DepartmentEnum";

const useUser = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [visibleForm, setVisibleForm] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [cache] = useState(() => new Map());
  const [roleCount, setRoleCount] = useState({});
  const [dealCount, setDealCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [infoShow, setInfoShow] = useState(false);
  const [users, setUsers] = useState([]);

  const [userForm, setUserForm] = useState({
    userId: "",
    name: "",
    email: "",
    empId: "",
    phone: "",
    role: "",
    team: [],
    status: true,
    isIvr: false,
  });

  const navigate = useNavigate();

  const getCacheKey = (page, search, role) => `${page}_${search}_${role || "ALL"}`;

  const getUserList = async ({
    search = searchTerm,
    page = 1,
    direction = "",
    pageToken: customToken = null,
    role = null,
  } = {}) => {
    const cacheKey = getCacheKey(page, search, role);
    const tokenForPage = customToken ?? pageTokens[page - 1] ?? null;

    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      setUserList(cachedData.users);
      setUserCount(cachedData.maxCount);
      setHasMore(cachedData.hasMore);
      setNextPageToken(cachedData.nextPageToken);
      setPrevPageToken(cachedData.prevPageToken);
      setCurrentPage(page);
      setPageToken(tokenForPage);
      return;
    }

    try {
      setLoading(true);
      const body = {
        limit: pageSize,
        searchKey: search.trim(),
        departments: [salesDepartmentLabel, managementDepartmentLabel],
        role: role ? role : null,
        startAfter: direction === "next" ? tokenForPage : null,
        prev: direction === "prev" ? tokenForPage : null,
        direction,
      };

      const response = await UserService.fetchUsers(body);
      const users = response.data.users || [];
      const maxCount = response.data.maxCount || 0;
      const nextToken = response.data?.pagination?.lastDocId || null;
      const prevToken = response.data?.pagination?.firstDocId || null;
      setRoleCount(response.data?.aggregation || {});
      const calculatedHasMore = response.data?.pagination?.hasMore || false;

      const cacheData = {
        users,
        maxCount,
        hasMore: calculatedHasMore,
        nextPageToken: nextToken,
        prevPageToken: prevToken,
      };

      cache.set(cacheKey, cacheData);

      if (direction === "next" && nextToken && calculatedHasMore) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length === page) {
            newTokens.push(nextToken);
          }
          return newTokens;
        });
      } else if (direction === "previous" && prevToken && page > 1) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
            newTokens[page - 1] = prevToken;
          }
          return newTokens;
        });
      }

      setUserList(users);
      setUserCount(maxCount);
      setHasMore(calculatedHasMore);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setPageToken(direction === "next" ? nextToken : prevToken);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching user list:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getUserList({ search: searchTerm, page: 1, role: selectedRole });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (token, direction) => {
    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      getUserList({
        search: searchTerm,
        pageToken: nextPageToken,
        page: newPage,
        direction: "next",
        role: selectedRole,
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      const prevToken = pageTokens[newPage - 1] || prevPageToken;
      getUserList({
        search: searchTerm,
        pageToken: prevToken,
        page: newPage,
        direction: "prev",
        role: selectedRole,
      });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  useEffect(() => {
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getUserList({ search: searchTerm, page: 1, role: selectedRole });
  }, [pageSize, selectedRole]);

  const handleUserDetails = async (userData) => {
    console.log("userData", userData);
    if (userData) {
      setUserForm({
        userId: userData.id || "",
        name: userData.name || "",
        email: userData.emailId || "",
        empId: userData.employeeId || "",
        phone: userData.phoneNumber || "",
        role: userData.role || "",
        team: userData.teamNames || [],
        status: userData.status !== undefined ? userData.status : true,
        isIvr: userData.isIvr || false,
      });
    }
  };

  const fetchUserCount = async () => {
    try {
      const response = await userServices.getUserCount();
      if (response.status === 200) {
        setRoleCount(response.data || {});
      } else {
        console.error("Failed to fetch user count:", response.data);
      }
    } catch (error) {
      console.error("Error fetching user count:", error);
    }
  };

  useEffect(() => {
    fetchUserCount();
  }, []);

  const handleUserCreate = async (userData) => {
    if (!userData.name || userData.name.trim() === "") {
      showErrorToast("Please enter a valid name.");
      return;
    }
    if (!userData.employeeId || userData.employeeId?.trim() === "") {
      showErrorToast("Please enter a valid employee ID.");
      return;
    }
    if (!userData.phoneNumber || !validateInput("phone", userData.phoneNumber)) {
      showErrorToast("Please enter a valid phone number.");
      return;
    }
    if (!userData.role || userData.role.trim() === "") {
      showErrorToast("Please select a valid role.");
      return;
    }
    const showTeamSelect = ["ADMIN", "CMO", "LEAD UPLOADER"].includes(userData.role.toUpperCase());
    if (!showTeamSelect && (!userData.team || userData.team.length === 0)) {
      showErrorToast("Please select a valid team.");
      return;
    }
    if (!validateInput("email", userData.emailId)) {
      showErrorToast("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const response = await userServices.createUser(userData);
      if (response.status === 200) {
        showSuccessToast(response.data.message || "User created successfully");
        setVisibleForm(false);
        setUserForm({
          userId: "",
          name: "",
          email: "",
          empId: "",
          phone: "",
          role: "",
          team: [],
          status: true,
          isIvr: false,
        });
        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        fetchUserCount();
        getUserList({ search: searchTerm, page: 1, role: selectedRole });
      } else {
        console.error("Failed to create user:", response.data);
      }
    } catch (error) {
      console.error("Error creating user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserUpdate = async (userId, userData) => {
    if (!userId || !userData) {
      showErrorToast("Invalid user ID or data.");
      return;
    }
    if (!userData.name || userData.name.trim() === "") {
      showErrorToast("Please enter a valid name.");
      return;
    }
    if (!userData.employeeId || userData.employeeId?.trim() === "") {
      showErrorToast("Please enter a valid employee ID.");
      return;
    }
    if (!userData.phoneNumber || !validateInput("phone", userData.phoneNumber)) {
      showErrorToast("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    try {
      const response = await userServices.updateUser(userId, userData);
      if (response.status === 200) {
        showSuccessToast(response.data.message || "User updated successfully");
        setVisibleForm(false);
        setUserForm({
          userId: "",
          name: "",
          email: "",
          empId: "",
          phone: "",
          role: "",
          team: [],
          status: true,
          isIvr: false,
        });
        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        getUserList({ search: searchTerm, page: 1, role: selectedRole });
      } else {
        console.error("Failed to update user:", response.data);
      }
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = async (userId, currentStatus) => {
    console.log("Toggling status for user ID:", userId, "Current status:", currentStatus);
    setLoading(true);
    try {
      const body = { uid: userId };
      const response = await userServices.checkUserDeactivation(body);
      if (response.status === 200) {
        const data = response.data;
        setDealCount(data.dealCount || 0);
        setLeadCount(data.leadCount || 0);
        setInfoShow(true);
        setUserForm((prev) => ({
          ...prev,
          userId,
          status: !currentStatus, // Update the userForm status for the confirmation modal
        }));
        setVisibleForm(false);
      } else {
        showErrorToast("Failed to check user deactivation status.");
      }
    } catch (error) {
      console.error("Error checking user deactivation:", error);
      showErrorToast("Error checking user status.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactiveUser = async (userId) => {
    setLoading(true);
    try {
      const body = {
        uid: userId,
        dealCount: dealCount,
        leadCount: leadCount,
        status: false,
      };
      const response = await UserService.deactivateUser(body);
      if (response.status === 200) {
        showSuccessToast(response.data.message || "User deactivated successfully");
        setInfoShow(false);
        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        getUserList({ search: searchTerm, page: 1, role: selectedRole });
        setUserForm({
          userId: "",
          name: "",
          email: "",
          empId: "",
          phone: "",
          role: "",
          team: [],
          status: true,
          isIvr: false,
        });
      } else {
        showErrorToast("Failed to deactivate user.");
      }
    } catch (error) {
      console.error("Error deactivating user:", error);
      showErrorToast("Error deactivating user.");
    } finally {
      setLoading(false);
    }
  };

  const handleReassignLead = (userId, incomingTeamId) => {
    const finalTeamId = Array.isArray(incomingTeamId) && incomingTeamId.length > 0 ? incomingTeamId[0] : "";
    navigate(`${getBasePathByUserRole()}/lead-reasign-lead`, {
      state: {
        userId: userId,
        teamId: finalTeamId,
        filterOpen: true,
      },
    });
  };

  const handleDealReassign = (userId) => {
    navigate(`${getBasePathByUserRole()}/pipline-user-deal`, {
      state: {
        userId: userId,
        userList: users,
      },
    });
  };

  const userData = async () => {
    try {
      const userResponse = await teamService.getUserName();
      setUsers(userResponse.data.users || []);
    } catch (error) {
      console.error("Error fetching user names:", error);
    }
  };

  useEffect(() => {
    if (leadCount || dealCount) {
      userData();
    }
  }, [leadCount, dealCount]);

  return {
    searchTerm,
    setSearchTerm,
    selectedRole,
    handleRoleChange,
    userList,
    userCount,
    pageSize,
    pageToken,
    nextPageToken,
    prevPageToken,
    currentPage,
    hasMore,
    loading,
    handleSearchSubmit,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    userForm,
    setUserForm,
    handleUserCreate,
    handleUserUpdate,
    visibleForm,
    setVisibleForm,
    handleUserDetails,
    handleToggleClick,
    roleCount,
    dealCount,
    leadCount,
    infoShow,
    setInfoShow,
    handleReassignLead,
    handleDealReassign,
    handleDeactiveUser,
    users,
  };
};

export default useUser;